import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle } from "lucide-react";
import type { PlayProps } from "@/types/puzzle";

export default function TranslateSentencePlay({ data, onAnswer, result }: PlayProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  const sourceSentence = (data.sourceSentence as string) ?? "";
  const sourceLanguage = (data.sourceLanguage as string) ?? "";
  const targetLanguage = (data.targetLanguage as string) ?? "";

  const answered = result !== null;
  const isCorrect = result?.correct ?? false;
  const correctTranslation = (result?.solution.correctTranslation as string) ?? "";

  const handleCheck = () => {
    if (!value.trim() || answered) return;
    onAnswer(value);
  };

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <h2 className="text-lg font-medium text-muted-foreground text-center animate-fade-in-up">
        {t("puzzleStudent.translateSentence.heading")}
      </h2>

      <div className="p-5 rounded-lg border border-border bg-card text-center animate-fade-in-up">
        <Badge variant="outline" className="mb-2 text-xs">
          {sourceLanguage}
        </Badge>
        <p className="text-xl font-display font-semibold text-foreground">
          {sourceSentence}
        </p>
      </div>

      <div className="space-y-2">
        <Badge variant="outline" className="text-xs">
          {targetLanguage}
        </Badge>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("puzzleStudent.translateSentence.placeholder", {
            language: targetLanguage,
          })}
          disabled={answered}
          rows={2}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !answered) {
              e.preventDefault();
              handleCheck();
            }
          }}
          autoFocus
          className="text-base font-medium"
        />
      </div>

      {!answered && (
        <Button onClick={handleCheck} disabled={!value.trim()} className="w-full h-12 text-base font-bold">
          {t("puzzleStudent.translateSentence.checkButton")}
        </Button>
      )}

      {answered && (
        <div
          className={`flex items-start gap-3 p-4 rounded-lg animate-fade-in-up ${
            isCorrect
              ? "bg-green-500/10 text-green-700"
              : "bg-red-500/10 text-red-700"
          }`}
        >
          {isCorrect ? (
            <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 animate-bounce-in" />
          ) : (
            <XCircle className="w-5 h-5 shrink-0 mt-0.5 animate-shake" />
          )}
          <div className="text-sm font-semibold">
            {isCorrect ? (
              t("puzzleStudent.translateSentence.correct")
            ) : (
              <>
                <p>{t("puzzleStudent.translateSentence.correctTranslationLabel")}</p>
                <p className="mt-1 font-normal">{correctTranslation}</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
